"use client";

import Link from "next/link";
import { useRouter } from "next/navigation";
import { useState } from "react";
import { useLanguage } from "@tillix/i18n/react";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@tillix/ui-atoms/atoms/alert";
import { Button } from "@tillix/ui-atoms/button";
import { Card } from "@tillix/ui-atoms/card";
import { cn } from "@tillix/ui-atoms/lib/utils";
import { CircleAlert, CircleCheck } from "lucide-react";
import { clearVerification } from "../actions";
import { useAppointmentT } from "../i18n/use-t";
import { errorKey } from "../lib/errors";
import {
  formatDateLabel,
  formatTimeLabel,
  isoToCompactDate,
  isoToCompactTime,
} from "../lib/sap-format";
import { getOrderActions } from "../lib/status";
import CancelDialog from "./CancelDialog";
import OrderRestrictionTooltip from "./OrderRestrictionTooltip";
import { StatusBadge } from "./StatusBadge";
import type { DashboardNotice } from "../lib/dashboard-notice";
import type { OrderSummary } from "../types";

interface OrderListProps {
  orders: OrderSummary[];
  /** Action error code when the order lookup failed; renders in place of the list. */
  loadError?: string;
  /** Success banner for the order a reschedule/cancel just changed. */
  notice?: DashboardNotice | null;
}

export default function OrderList({
  orders,
  loadError,
  notice,
}: OrderListProps) {
  const t = useAppointmentT();
  const { language } = useLanguage();
  const router = useRouter();
  const [switching, setSwitching] = useState(false);

  const handleSwitchAccount = async () => {
    setSwitching(true);
    await clearVerification();
    router.refresh();
  };

  const header = (
    <div className="flex flex-wrap items-baseline justify-between gap-2">
      <h1 className="font-semibold text-xl">{t("dashboard.title")}</h1>
      <p className="text-sm">{t("dashboard.description")}</p>
      <Button
        className="h-auto p-0 text-sm underline underline-offset-3 transition-[text-underline-offset] hover:underline-offset-2"
        disabled={switching}
        onClick={handleSwitchAccount}
        variant="link"
      >
        {t("dashboard.switchAccount")}
      </Button>
    </div>
  );

  const banner = notice && (
    <Alert variant="success">
      <CircleCheck />
      <AlertTitle>
        {t("order.number", { orderNumber: notice.orderNumber })}
      </AlertTitle>
      <AlertDescription>
        {t(`dashboard.banners.${notice.kind}`)}
      </AlertDescription>
    </Alert>
  );

  if (loadError !== undefined) {
    return (
      <div className="flex flex-col gap-4">
        {header}
        {banner}
        <Alert variant="error">
          <CircleAlert />
          <AlertDescription>
            {t(errorKey("dashboard.errors", loadError))}
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  if (orders.length === 0) {
    return (
      <div className="flex flex-col gap-4">
        {header}
        {banner}
        <Card>
          <Card.Content className="py-8 text-center text-muted-foreground">
            {t("dashboard.empty")}
          </Card.Content>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {header}
      {banner}
      {orders.map((order) => {
        const actions = getOrderActions(order.status, order.priority);
        const { canReschedule, canCancel } = actions;
        // "Your updated appointment details are shown below" — point at them.
        const isHighlighted =
          notice?.kind === "rescheduled" &&
          notice.orderNumber === order.orderNumber;
        return (
          <Card
            className={cn(isHighlighted && "border-success")}
            key={order.orderNumber}
          >
            <Card.Content className="flex flex-wrap items-center justify-between gap-4 py-4">
              <div className="flex flex-col gap-1">
                <span className="font-medium">
                  {t("order.number", { orderNumber: order.orderNumber })}
                </span>
                <span className="text-muted-foreground text-sm">
                  {t("order.scheduledForWindow", {
                    date: formatDateLabel(
                      isoToCompactDate(order.scheduleStartDate),
                      language,
                    ),
                    start: formatTimeLabel(
                      isoToCompactTime(order.scheduleStartTime),
                      language,
                    ),
                    end: formatTimeLabel(
                      isoToCompactTime(order.scheduleEndTime),
                      language,
                    ),
                  })}
                </span>
              </div>
              <div className="flex gap-3 max-sm:w-full max-sm:flex-col sm:items-center">
                <div className="flex items-center gap-2">
                  <OrderRestrictionTooltip actions={actions} />
                  <StatusBadge status={order.status} />
                </div>
                {canReschedule && (
                  <Button
                    render={(props) => (
                      <Link
                        {...props}
                        href={`/appointment/${order.orderNumber}`}
                      >
                        {t("dashboard.reschedule")}
                      </Link>
                    )}
                    size="sm"
                    variant="outline"
                  />
                )}
                {canCancel && (
                  <CancelDialog
                    needsContact={!order.hasContactInfo}
                    orderNumber={order.orderNumber}
                  />
                )}
              </div>
            </Card.Content>
          </Card>
        );
      })}
    </div>
  );
}
